import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import apiClient from '@/lib/apiClient';
import { queryKeys } from '@/lib/queryKeys';
import { ENGINES } from '@/lib/evalConstants';

interface EntitlementsResponse {
  allowed_engines: string[];
  prompt_limit: number;
  active_prompt_count: number;
  governed: boolean;
  query_fanout_enabled?: boolean;
  agent_journeys_enabled?: boolean;
  agent_mirror_enabled?: boolean;
  agent_delivery_access_source?: string;
  hosted_page_limit?: number | null;
  hosted_page_used?: number;
  hosted_page_remaining?: number | null;
  managed_optimization_enabled?: boolean;
  beta?: boolean;
  fact_ledger_beta_enabled?: boolean;
}

interface UseEntitlementsResult {
  allowedEngines: string[];
  promptLimit: number | null;
  activePromptCount: number | null;
  governed: boolean;
  /** Query Fan Out beta capability. FALSE while loading and on error so the
   *  toggle appears only after the default-on backend entitlement is healthy.
   *  The endpoint enforces the same kill switch; this only hides the toggle. */
  queryFanoutEnabled: boolean;
  /** Agent Journeys (journeys + Surface Packs). Same fail-closed default as
   *  `queryFanoutEnabled`; the routers enforce the same flag. */
  agentJourneysEnabled: boolean;
  /** Agent Mirror (managed edge delivery). Fail-closed like the two above. */
  agentMirrorEnabled: boolean;
  agentDeliveryAccessSource: string;
  hostedPageLimit: number | null;
  hostedPageUsed: number;
  hostedPageRemaining: number | null;
  /** Managed updates (the Site › Updates view). Fail-closed like the other
   *  capability flags. */
  managedOptimizationEnabled: boolean;
  /** The workspace's account is in the staff-set beta program. Default false. */
  beta: boolean;
  /** Opt-in Fact Ledger Records beta. False while loading or on error. */
  factLedgerBetaEnabled: boolean;
  isLoading: boolean;
  isError: boolean;
  retry: () => Promise<unknown>;
}

/**
 * Canonical `GET /workspaces/{id}/entitlements` hook — the plan-gated set of
 * engines this workspace may run (e.g. Claude excluded on free tier). Defaults
 * to ALL engines while loading so nothing is wrongly disabled mid-load; the
 * backend still enforces (403) regardless of what the picker allows.
 */
export function useEntitlements(workspaceId: string | null | undefined): UseEntitlementsResult {
  const query: UseQueryResult<EntitlementsResponse, Error> = useQuery({
    queryKey: queryKeys.workspace.entitlements(workspaceId ?? ''),
    queryFn: () =>
      apiClient.get<EntitlementsResponse>(`/workspaces/${workspaceId}/entitlements`),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  return {
    allowedEngines: query.data?.allowed_engines ?? [...ENGINES],
    promptLimit: query.data?.prompt_limit ?? null,
    activePromptCount: query.data?.active_prompt_count ?? null,
    governed: query.data?.governed ?? false,
    queryFanoutEnabled: query.data?.query_fanout_enabled ?? false,
    agentJourneysEnabled: query.data?.agent_journeys_enabled ?? false,
    agentMirrorEnabled: query.data?.agent_mirror_enabled ?? false,
    agentDeliveryAccessSource: query.data?.agent_delivery_access_source ?? 'default_off',
    hostedPageLimit: query.data?.hosted_page_limit ?? 0,
    hostedPageUsed: query.data?.hosted_page_used ?? 0,
    hostedPageRemaining: query.data?.hosted_page_remaining ?? 0,
    managedOptimizationEnabled: query.data?.managed_optimization_enabled ?? false,
    beta: query.data?.beta ?? false,
    factLedgerBetaEnabled: query.data?.fact_ledger_beta_enabled ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    retry: query.refetch,
  };
}
